import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, Save } from "lucide-react";
import { tipoCostoInfo } from "@/lib/tipoCosto";

const daScegliere = (it) => (it.da_scegliere == null ? it.tipo_costo === "fornitura" : !!it.da_scegliere);

// Scelta materiali: parte dalle voci del preventivo, si spuntano solo quelle in cui il cliente deve scegliere (finiture).
export default function MaterialiCliente({ wf, cid, reload }) {
  const items = wf.computo_metrico?.items || [];
  const [stato, setStato] = useState({});
  const [soloScelte, setSoloScelte] = useState(false);
  const [busy, setBusy] = useState(false);
  const firmato = !!wf.materiali_scelta?.firmato_cliente;
  useEffect(() => {
    setStato(Object.fromEntries(items.map((it) => [it.id, { da_scegliere: daScegliere(it), materiale_scelto: it.materiale_scelto || "", materiale_note: it.materiale_note || "" }])));
    // eslint-disable-next-line
  }, [JSON.stringify(items.map((i) => [i.id, i.da_scegliere, i.materiale_scelto, i.materiale_note]))]);
  const upd = (id, k, v) => setStato({ ...stato, [id]: { ...stato[id], [k]: v } });
  const salva = async () => {
    setBusy(true);
    try {
      await api.patch(`/commesse/${cid}/workflow/computo-items`, { updates: Object.entries(stato).map(([id, v]) => ({ id, ...v })) });
      toast.success("Scelta materiali salvata"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const firma = async () => {
    await api.post(`/commesse/${cid}/workflow/materiali`, { items: wf.materiali_scelta?.items || [], firmato_cliente: !firmato, firma_data: !firmato ? new Date().toISOString().slice(0, 10) : null });
    toast.success(!firmato ? "Segnata come firmata dal cliente" : "Firma rimossa"); reload();
  };
  const vis = items.filter((it) => !soloScelte || stato[it.id]?.da_scegliere);
  const nScelte = items.filter((it) => stato[it.id]?.da_scegliere).length;
  return (
    <div className="space-y-3" data-testid="materiali-cliente">
      <div className="bg-white border border-zinc-200 rounded p-3 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-semibold">Materiali da far scegliere al cliente</h3>
          <p className="text-xs text-zinc-500">Le voci vengono dal preventivo. Spunta solo quelle in cui il cliente sceglie (tinta, porte, infissi, piastrelle, placchette…). I materiali di consumo e i servizi restano esclusi.</p>
        </div>
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={soloScelte} onChange={(e) => setSoloScelte(e.target.checked)} data-testid="mat-solo-scelte" />Mostra solo da scegliere ({nScelte})</label>
        <Button size="sm" variant="outline" onClick={() => window.open(`/commesse/${cid}/stampa/materiali`, "_blank")} data-testid="mat-stampa"><Printer className="h-4 w-4 mr-1" />Stampa per il cliente</Button>
        <Button size="sm" variant={firmato ? "default" : "outline"} onClick={firma} data-testid="mat-firmato">{firmato ? "Firmata dal cliente ✓" : "Segna firmata"}</Button>
        <Button size="sm" onClick={salva} disabled={busy} style={{ background: "var(--brand)", color: "white" }} data-testid="mat-save"><Save className="h-4 w-4 mr-1" />Salva</Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500"><tr>
            <th className="px-2 py-2 w-20">Da scegliere</th><th className="px-2 text-left">Voce del preventivo</th><th className="px-2 text-right">Q.tà</th>
            <th className="px-2 text-left w-72">Materiale scelto</th><th className="px-2 text-left w-56">Note</th>
          </tr></thead>
          <tbody>
            {vis.map((it) => {
              const s = stato[it.id] || {};
              const t = tipoCostoInfo(it.tipo_costo);
              return (
                <tr key={it.id} className={`border-t ${s.da_scegliere ? "" : "text-zinc-400"}`} data-testid={`mat-riga-${it.id}`}>
                  <td className="px-2 text-center"><input type="checkbox" checked={!!s.da_scegliere} onChange={(e) => upd(it.id, "da_scegliere", e.target.checked)} data-testid={`mat-scegli-${it.id}`} /></td>
                  <td className="px-2 py-1.5"><div className="font-medium">{it.name}</div><span className={`text-[9px] px-1.5 rounded ${t.cls}`}>{t.short}</span></td>
                  <td className="px-2 text-right mono whitespace-nowrap">{it.qty} {it.unit}</td>
                  <td className="px-2 py-1">{s.da_scegliere && <Input value={s.materiale_scelto} onChange={(e) => upd(it.id, "materiale_scelto", e.target.value)} placeholder="es. Gres 60×60 Marazzi grigio" className="h-8 text-xs" data-testid={`mat-scelto-${it.id}`} />}</td>
                  <td className="px-2 py-1">{s.da_scegliere && <Input value={s.materiale_note} onChange={(e) => upd(it.id, "materiale_note", e.target.value)} placeholder="colore, codice, posa…" className="h-8 text-xs" data-testid={`mat-note-${it.id}`} />}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
